import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";
import {
  CREATE_PHARMACY_APPOINTMENT,
  UPDATE_PHARMACY_APPOINTMENT,
  DELETE_PHARMACY_APPOINTMENT,
  GET_MY_PHARMACY_APPOINTMENTS,
  GET_MY_PHARMACY_BOOKINGS,
  UPDATE_BOOKING_STATUS,
  CANCEL_PHARMACY_BOOKING,
  BOOK_APPOINTMENT_FOR_USER,
  GET_APPOINTMENT_TIME_SLOTS,
  GET_APPOINTMENT_OPEN_DAYS,
} from "../../services/urls";
import { getPharmacyId } from "../../utils/commonFunctions";
import { GET_USERS_URL } from "../../services/urls";

// Initial state
const initialState = {
  loading: false,
  bookingsListLoading: false,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,
  bookingLoading: false,
  appointments: [],
  bookings: [],
  totalBookings: 0,
  usersList: [],
  usersListLoading: false,
  error: null,
  createError: null,
  updateError: null,
  deleteError: null,
  bookingError: null,
};

// Async thunk to fetch pharmacy appointments
export const fetchPharmacyAppointments = createAsyncThunk(
  "pharmacyAppointments/fetchAppointments",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(GET_MY_PHARMACY_APPOINTMENTS.replace(":pharmacyId", getPharmacyId()));
      return response.data.data || response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch appointments"
      );
    }
  }
);

// Async thunk to create pharmacy appointment
export const createPharmacyAppointment = createAsyncThunk(
  "pharmacyAppointments/createAppointment",
  async (appointmentData, { rejectWithValue }) => {
    try {
      const response = await api.post(CREATE_PHARMACY_APPOINTMENT.replace(":pharmacyId", getPharmacyId()), appointmentData);
      return response.data.data || response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create appointment"
      );
    }
  }
);

// Async thunk to update pharmacy appointment
export const updatePharmacyAppointment = createAsyncThunk(
  "pharmacyAppointments/updateAppointment",
  async ({ appointmentId, updateData }, { rejectWithValue }) => {
    try {
      const response = await api.put(
        `${UPDATE_PHARMACY_APPOINTMENT}/${appointmentId}`,
        updateData
      );
      return response.data.data || response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update appointment"
      );
    }
  }
);

// Async thunk to delete pharmacy appointment
export const deletePharmacyAppointment = createAsyncThunk(
  "pharmacyAppointments/deleteAppointment",
  async (appointmentId, { rejectWithValue }) => {
    try {
      await api.delete(`${DELETE_PHARMACY_APPOINTMENT}/${appointmentId}`);
      return appointmentId;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete appointment"
      );
    }
  }
);

// Async thunk to fetch pharmacy bookings
export const fetchPharmacyBookings = createAsyncThunk(
  "pharmacyAppointments/fetchBookings",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();

      const cappedLimit =
        filters.limit != null && filters.limit !== ""
          ? Math.min(100, Math.max(1, Number(filters.limit)))
          : null;

      
      // Add filters
      if (typeof filters.status === 'string' && filters.status.length > 0) queryParams.append('status', filters.status);
      if (filters.dateFilter) queryParams.append('dateFilter', filters.dateFilter);
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);
      if (filters.page) queryParams.append('page', filters.page);
      if (cappedLimit != null && !Number.isNaN(cappedLimit)) queryParams.append("limit", String(cappedLimit));
      
      const url = `${GET_MY_PHARMACY_BOOKINGS.replace(":pharmacyId", getPharmacyId())}?${queryParams}`;
      const response = await api.get(url);
      return response.data.data || response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch bookings"
      );
    }
  }
);

// Async thunk to update booking status
export const updateBookingStatus = createAsyncThunk(
  "pharmacyAppointments/updateBookingStatus",
  async ({ bookingId, status, timestamps = {} }, { rejectWithValue }) => {
    try {
      const response = await api.put(
        `${UPDATE_BOOKING_STATUS}/${bookingId}/status`,
        {
          status,
          startedAt: timestamps.startedAt,
          completedAt: timestamps.completedAt,
        }
      );
      return response.data.data || response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update booking status"
      );
    }
  }
);

// Async thunk to cancel booking
export const cancelPharmacyBooking = createAsyncThunk(
  "pharmacyAppointments/cancelBooking",
  async ({ bookingId, reason }, { rejectWithValue }) => {
    try {
      const response = await api.put(
        `${CANCEL_PHARMACY_BOOKING}/${bookingId}/cancel`,
        { reason }
      );
      return response.data.data || response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to cancel booking"
      );
    }
  }
);

// Async thunk to search users
export const fetchUsersWithNameOrEmail = createAsyncThunk(
  "pharmacyAppointments/fetchUsersWithNameOrEmail",
  async (search = "", { rejectWithValue }) => {
    try {
      const response = await api.post(GET_USERS_URL, {
        query: encodeURI(search),
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch users"
      );
    }
  }
);

function parsePharmacyBookingsList(payload) {
  const raw = payload?.bookings ?? payload;
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === "object" && Array.isArray(raw.data)) return raw.data;
  return [];
}

/** Fetch bookings for a date range without updating list state (slot conflict checks). */
export const fetchPharmacyBookingsForSlotCheck = createAsyncThunk(
  "pharmacyAppointments/fetchBookingsForSlotCheck",
  async ({ startDate, endDate }, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (startDate) queryParams.append("startDate", startDate);
      if (endDate) queryParams.append("endDate", endDate);
      queryParams.append("limit", "100");
      queryParams.append("page", "1");

      const url = `${GET_MY_PHARMACY_BOOKINGS.replace(":pharmacyId", getPharmacyId())}?${queryParams}`;
      const response = await api.get(url);
      return parsePharmacyBookingsList(response.data.data || response.data);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch bookings"
      );
    }
  }
);

export function normalizePharmacyBookingTime(timeStr) {
  if (!timeStr) return "";
  return String(timeStr).slice(0, 5);
}

export function isPharmacyBookingSlotActive(status) {
  return status !== "cancelled";
}

export function getPharmacyBookingCustomerLabel(booking) {
  if (booking?.userId) {
    const first = booking.userId.firstName || "";
    const last = booking.userId.lastName || "";
    const name = `${first} ${last}`.trim();
    return name || booking.userId.email || "—";
  }
  if (booking?.userDetails) {
    const first = booking.userDetails.firstName || "";
    const last = booking.userDetails.lastName || "";
    const name = `${first} ${last}`.trim();
    return name || booking.userDetails.email || "—";
  }
  return booking?.fullName || "Guest User";
}

export function findPharmacyBookingsInSlot(bookings, dateStr, timeStr) {
  const normalizedTime = normalizePharmacyBookingTime(timeStr);
  if (!dateStr || !normalizedTime || !Array.isArray(bookings)) return [];

  return bookings.filter((booking) => {
    if (!isPharmacyBookingSlotActive(booking?.status)) return false;
    if (booking.bookingDateString !== dateStr) return false;
    return normalizePharmacyBookingTime(booking.bookingTimeString) === normalizedTime;
  });
}

export const fetchAppointmentTimeSlots = createAsyncThunk(
  "pharmacyAppointments/fetchAppointmentTimeSlots",
  async (
    { appointmentId, date, isBookingByPharmacy = true },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.get(GET_APPOINTMENT_TIME_SLOTS, {
        params: {
          appointmentId,
          date,
          isBookingByPharmacy,
        },
      });
      const payload = response.data?.data ?? response.data;
      if (Array.isArray(payload)) return payload;
      if (Array.isArray(payload?.slots)) return payload.slots;
      if (Array.isArray(payload?.timeSlots)) return payload.timeSlots;
      return [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch time slots"
      );
    }
  }
);

export const fetchAppointmentOpenDays = createAsyncThunk(
  "pharmacyAppointments/fetchAppointmentOpenDays",
  async ({ appointmentId, month, year }, { rejectWithValue }) => {
    try {
      const url = GET_APPOINTMENT_OPEN_DAYS.replace(
        ":pharmacyId",
        getPharmacyId()
      );
      const response = await api.get(url, {
        params: { month, year, appointmentId },
      });
      const data = response.data?.data ?? response.data ?? [];
      const dates = Array.isArray(data)
        ? data
        : Array.isArray(data?.dates)
        ? data.dates
        : [];
      return { month, year, dates, appointmentId };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch open days"
      );
    }
  }
);

// Async thunk to book appointment for user
export const bookAppointmentForUser = createAsyncThunk(
  "pharmacyAppointments/bookForUser",
  async (bookingData, { rejectWithValue }) => {
    try {
      const response = await api.post(
        BOOK_APPOINTMENT_FOR_USER.replace(":pharmacyId", getPharmacyId()),
        bookingData
      );
      return response.data.data || response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to book appointment"
      );
    }
  }
);

// Create slice
const pharmacyAppointmentsSlice = createSlice({
  name: "pharmacyAppointments",
  initialState,
  reducers: {
    clearAppointments: (state) => {
      state.appointments = [];
    },
    clearBookings: (state) => {
      state.bookings = [];
    },
    clearErrors: (state) => {
      state.error = null;
      state.createError = null;
      state.updateError = null;
      state.deleteError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch appointments
      .addCase(fetchPharmacyAppointments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPharmacyAppointments.fulfilled, (state, action) => {
        state.loading = false;
        state.appointments = action.payload;
      })
      .addCase(fetchPharmacyAppointments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Create appointment
      .addCase(createPharmacyAppointment.pending, (state) => {
        state.createLoading = true;
        state.createError = null;
      })
      .addCase(createPharmacyAppointment.fulfilled, (state, action) => {
        state.createLoading = false;
        state.appointments.push(action.payload);
      })
      .addCase(createPharmacyAppointment.rejected, (state, action) => {
        state.createLoading = false;
        state.createError = action.payload;
      })
      
      // Update appointment
      .addCase(updatePharmacyAppointment.pending, (state) => {
        state.updateLoading = true;
        state.updateError = null;
      })
      .addCase(updatePharmacyAppointment.fulfilled, (state, action) => {
        state.updateLoading = false;
        const index = state.appointments.findIndex(
          (apt) => apt._id === action.payload._id
        );
        if (index !== -1) {
          state.appointments[index] = action.payload;
        }
      })
      .addCase(updatePharmacyAppointment.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateError = action.payload;
      })
      
      // Delete appointment
      .addCase(deletePharmacyAppointment.pending, (state) => {
        state.deleteLoading = true;
        state.deleteError = null;
      })
      .addCase(deletePharmacyAppointment.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.appointments = state.appointments.filter(
          (apt) => apt._id !== action.payload
        );
      })
      .addCase(deletePharmacyAppointment.rejected, (state, action) => {
        state.deleteLoading = false;
        state.deleteError = action.payload;
      })
      
      // Fetch bookings (separate flag so calendar / filter refetches do not hide the whole page)
      .addCase(fetchPharmacyBookings.pending, (state) => {
        state.bookingsListLoading = true;
        state.error = null;
      })
      .addCase(fetchPharmacyBookings.fulfilled, (state, action) => {
        state.bookingsListLoading = false;
        state.bookings = action.payload.bookings || action.payload;
        state.totalBookings = action.payload.total || action.payload.length;
      })
      .addCase(fetchPharmacyBookings.rejected, (state, action) => {
        state.bookingsListLoading = false;
        state.error = action.payload;
      })
      
      // Update booking status
      .addCase(updateBookingStatus.pending, (state) => {
        state.updateLoading = true;
        state.updateError = null;
      })
      .addCase(updateBookingStatus.fulfilled, (state, action) => {
        state.updateLoading = false;
        const index = state.bookings.findIndex(
          (booking) => booking._id === action.payload._id
        );
        if (index !== -1) {
          state.bookings[index] = action.payload;
        }
      })
      .addCase(updateBookingStatus.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateError = action.payload;
      })
      
      // Cancel booking
      .addCase(cancelPharmacyBooking.pending, (state) => {
        state.updateLoading = true;
        state.updateError = null;
      })
      .addCase(cancelPharmacyBooking.fulfilled, (state, action) => {
        state.updateLoading = false;
        const index = state.bookings.findIndex(
          (booking) => booking._id === action.payload._id
        );
        if (index !== -1) {
          state.bookings[index] = action.payload;
        }
      })
      .addCase(cancelPharmacyBooking.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateError = action.payload;
      })
      
      // Book appointment for user
      .addCase(bookAppointmentForUser.pending, (state) => {
        state.bookingLoading = true;
        state.bookingError = null;
      })
      .addCase(bookAppointmentForUser.fulfilled, (state, action) => {
        state.bookingLoading = false;
        state.bookings.unshift(action.payload);
        state.totalBookings += 1;
      })
      .addCase(bookAppointmentForUser.rejected, (state, action) => {
        state.bookingLoading = false;
        state.bookingError = action.payload;
      })
      
      // Fetch Users With Name Or Email
      .addCase(fetchUsersWithNameOrEmail.pending, (state) => {
        state.usersListLoading = true;
      })
      .addCase(fetchUsersWithNameOrEmail.fulfilled, (state, action) => {
        state.usersListLoading = false;
        state.usersList = action.payload;
      })
      .addCase(fetchUsersWithNameOrEmail.rejected, (state, action) => {
        state.usersListLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearAppointments, clearBookings, clearErrors } = pharmacyAppointmentsSlice.actions;
export default pharmacyAppointmentsSlice.reducer;
